import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("axentia_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses (skip for login endpoint)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes("/auth/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("axentia_token");
      localStorage.removeItem("axentia_username");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) =>
    api.post("/auth/login", { username, password }),
  verify: () => api.get("/auth/verify"),
};

export const modulesAPI = {
  getAll: () => api.get("/modules"),
  getOne: (id) => api.get(`/modules/${id}`),
};

export const progressAPI = {
  get: () => api.get("/progress"),
  update: (module_id, section, completed = true) =>
    api.post("/progress/update", { module_id, section, completed }),
};

export const assessmentAPI = {
  submit: (module_id, answers) =>
    api.post(`/assessments/${module_id}/submit`, { module_id, answers }),
  aiGrade: (module_id, answers) =>
    api.post(`/assessments/${module_id}/ai-grade`, { module_id, answers }),
};

export const certificationAPI = {
  get: () => api.get("/certification"),
  downloadUrl: () => `${API_BASE}/certificate/download`,
  generateVerification: () => api.post("/certificate/generate"),
};

export const searchAPI = {
  search: (q) => api.get(`/search?q=${encodeURIComponent(q)}`),
};

export const templateAPI = {
  downloadUrl: (moduleId, templateIndex) =>
    `${API_BASE}/templates/${moduleId}/${templateIndex}/download`,
  downloadPdfUrl: (moduleId, templateIndex) =>
    `${API_BASE}/templates/${moduleId}/${templateIndex}/download/pdf`,
  downloadDocxUrl: (moduleId, templateIndex) =>
    `${API_BASE}/templates/${moduleId}/${templateIndex}/download/docx`,
};

export const certificateAPI = {
  get: () => api.get("/certification"),
  download: () => api.get("/certificate/download", { responseType: "blob" }),
  generate: () => api.post("/certificate/generate"),
  // Public endpoint - no auth needed
  verify: (verificationId) => axios.get(`${API_BASE}/certificate/verify/${verificationId}`),
};

export const adminAPI = {
  getUsers: () => api.get("/admin/users"),
  createUser: (data) => api.post("/admin/users", data),
  updateUser: (username, data) => api.put(`/admin/users/${username}`, data),
  deleteUser: (username) => api.delete(`/admin/users/${username}`),
  createModule: (data) => api.post("/admin/modules", data),
  updateModule: (id, data) => api.put(`/admin/modules/${id}`, data),
  deleteModule: (id) => api.delete(`/admin/modules/${id}`),
  getAnalytics: () => api.get("/admin/analytics"),
  updatePromotion: (username, level, notes) =>
    api.put(`/admin/promotion/${username}`, { username, level, notes }),
  updateKPIs: (username, period, kpis) =>
    api.post(`/admin/kpis/${username}`, { username, period, kpis }),
  getKPIThresholds: () => api.get("/admin/kpi-thresholds"),
  updateKPIThreshold: (level, thresholds) =>
    api.put("/admin/kpi-thresholds", { level, thresholds }),
  updateReadiness: (username, status) =>
    api.put(`/admin/users/${username}/readiness`, { username, status }),
  // Partner Track Admin
  updatePartnerTrackStage: (username, stage, notes) =>
    api.put("/admin/partner-track/stage", { username, stage, notes }),
  validatePartnerTrack: (username, passed, notes) =>
    api.post("/admin/partner-track/validate", { username, passed, notes }),
  getPartnerTrackFlagged: () => api.get("/admin/partner-track/flagged"),
  updatePartnerTrackCriteria: (stage, criteria) =>
    api.put("/admin/partner-track/criteria", { stage, criteria }),
};

export const promotionAPI = {
  getLevels: () => api.get("/promotion/levels"),
  getStatus: (username) => api.get(`/promotion/status/${username}`),
};

export const partnerTrackAPI = {
  getCriteria: () => api.get("/partner-track/criteria"),
  getStatus: (username) => api.get(`/partner-track/status/${username}`),
};

export const timeTrackAPI = {
  track: (module_id, seconds) =>
    api.post("/progress/track-time", { module_id, seconds }),
};

export default api;
